/**
 * Progress Display Component
 *
 * Zeigt Timer, Progress Bar und geschätzte Restzeit an
 */

import { Clock, Timer } from 'lucide-react';
import { Progress } from '@/components/v2/ui';

export interface ProgressDisplayProps {
  /** Verstrichene Zeit in Sekunden */
  elapsed: number;
  /** Durchschnittliche Generierungszeit in Sekunden */
  averageDuration: number;
  /** Ob Stats verfügbar sind */
  hasStats: boolean;
}

export function ProgressDisplay({ elapsed, averageDuration, hasStats }: ProgressDisplayProps) {
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs}s`;
  };

  const progressPercent = hasStats && averageDuration > 0
    ? Math.min(100, (elapsed / averageDuration) * 100)
    : 0;

  const remainingSeconds = hasStats && averageDuration > elapsed
    ? Math.ceil(averageDuration - elapsed)
    : 0;

  return (
    <>
      {/* Timer */}
      <div className="flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400">
        <Clock className="h-4 w-4" />
        <span className="font-mono">{formatTime(elapsed)}</span>
      </div>

      {/* Progress Bar & Estimated Time - nur wenn Stats verfügbar */}
      {hasStats && (
        <div className="w-full max-w-xs space-y-2">
          {/* Progress Bar */}
          <Progress
            value={progressPercent}
            size="sm"
            className="bg-orange-200/50 dark:bg-orange-900/30"
          />

          {/* Estimated Time Remaining */}
          {remainingSeconds > 0 && (
            <div className="flex items-center justify-center gap-1.5 text-xs text-orange-600 dark:text-orange-400">
              <Timer className="h-3.5 w-3.5" />
              <span className="font-medium">
                Estimated: ~{formatTime(remainingSeconds)} remaining
              </span>
            </div>
          )}
        </div>
      )}
    </>
  );
}
